import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { X, Package, CheckCircle2, AlertTriangle, Camera, FileImage } from "lucide-react";
import { AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import api from "../lib/api";
import { useTranslation } from "react-i18next";

export default function GoodsReceiptModal({ poId, onClose, onSuccess }) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [po, setPo] = useState(null);
  const [items, setItems] = useState([]);
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [deliveryNoteUrl, setDeliveryNoteUrl] = useState(null);

  useEffect(() => {
    api.get(`/purchase-orders/${poId}`).then((r) => {
      setPo(r.data);
      setItems(r.data.items.map((it) => ({
        supply_id: it.supply_id,
        supply_name: it.supply_name,
        unit: it.unit,
        quantity_ordered: it.quantity,
        quantity_received: it.quantity,
      })));
      setLoading(false);
    });
  }, [poId]);

  const updateQty = (idx, val) => {
    const next = [...items];
    next[idx] = { ...next[idx], quantity_received: Math.max(0, parseFloat(val) || 0) };
    setItems(next);
  };

  const handlePhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error(t("receiptModal.onlyImages"));
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error(t("receiptModal.photoMaxSize"));
      return;
    }
    setPhotoFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => setPhotoPreview(ev.target.result);
    reader.readAsDataURL(file);

    setUploadingPhoto(true);
    try {
      const dataUrl = await new Promise((resolve, reject) => {
        const r = new FileReader();
        r.onload = (ev) => resolve(ev.target.result);
        r.onerror = reject;
        r.readAsDataURL(file);
      });
      const { data } = await api.post(`/uploads/remito`, {
        purchase_order_id: poId,
        image_data: dataUrl,
      });
      setDeliveryNoteUrl(data.url);
      toast.success(t("receiptModal.photoAttachedToast"));
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("receiptModal.photoUploadError"));
      setPhotoFile(null);
      setPhotoPreview(null);
    } finally {
      setUploadingPhoto(false);
    }
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      await api.post(`/goods-receipts`, {
        purchase_order_id: poId,
        items: items.map((it) => ({
          supply_id: it.supply_id,
          quantity_ordered: it.quantity_ordered,
          quantity_received: it.quantity_received,
        })),
        notes: notes || undefined,
        remito_url: deliveryNoteUrl || undefined,
      });
      toast.success(t("receiptModal.submit"));
      onSuccess?.();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("common.error"));
    } finally {
      setSubmitting(false);
    }
  };

  const hasDiffs = items.some((it) => Math.abs(it.quantity_received - it.quantity_ordered) > 0.001);

  return (
    <AnimatePresence>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
        <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }}
          className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
          <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <div className="w-10 h-10 rounded-xl bg-[#FFDF00] flex items-center justify-center">
                <Package className="w-5 h-5 text-[#1A1A1A]" />
              </div>
              <div>
                <h2 className="font-display font-bold text-lg">{t("receiptModal.title", { folio: po?.folio || "" })}</h2>
                {po && (<p className="text-xs text-slate-500">{t("receiptModal.subtitle", { supplier: po.supplier?.business_name, branch: po.branch?.name })}</p>)}
              </div>
            </div>
            <button onClick={onClose} className="w-9 h-9 rounded-xl hover:bg-slate-100 flex items-center justify-center">
              <X className="w-4 h-4" />
            </button>
          </div>

          <div className="overflow-y-auto p-6 space-y-4 flex-1">
            {loading ? (
              <div className="text-center py-10 text-sm text-slate-400">{t("common.loading")}</div>
            ) : (
              <>
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 flex items-start gap-2.5">
                  <CheckCircle2 className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div className="text-xs text-blue-900">
                    <span className="font-bold">{t("receiptModal.receivingNote")}.</span> {t("receiptModal.receivingNoteBody")}
                  </div>
                </div>

                <div className="space-y-2">
                  {items.map((it, idx) => {
                    const diff = it.quantity_received - it.quantity_ordered;
                    const hasDiff = Math.abs(diff) > 0.001;
                    return (
                      <div key={idx} className="bg-slate-50 rounded-xl p-3">
                        <div className="font-semibold text-sm text-[#1A1A1A]">{it.supply_name}</div>
                        <div className="text-[11px] text-slate-500 mb-2">{t("receiptModal.unit", { unit: it.unit })}</div>
                        <div className="grid grid-cols-3 gap-3 items-end">
                          <div>
                            <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">{t("receiptModal.ordered")}</div>
                            <div className="text-xl font-display font-bold font-mono">{it.quantity_ordered}</div>
                          </div>
                          <div>
                            <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">{t("receiptModal.received")}</div>
                            <input data-testid={`rec-qty-input-${idx}`} type="number" min="0" step="0.1" value={it.quantity_received}
                              onChange={(e) => updateQty(idx, e.target.value)}
                              className={`w-full px-3 py-2 bg-white border-2 rounded-lg text-base font-mono font-bold focus:outline-none focus:ring-2 ${
                                hasDiff ? "border-amber-300 focus:ring-amber-300" : "border-emerald-300 focus:ring-emerald-300"
                              }`} />
                          </div>
                          <div>
                            <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">{t("receiptModal.difference")}</div>
                            <div className={`text-xl font-display font-bold font-mono ${hasDiff ? (diff > 0 ? "text-amber-700" : "text-red-700") : "text-emerald-700"}`}>
                              {diff > 0 ? "+" : ""}{diff.toFixed(1)}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div>
                  <label className="text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-1.5 block">{t("receiptModal.notes")}</label>
                  <textarea data-testid="rec-notes-input" value={notes} onChange={(e) => setNotes(e.target.value)} rows={2}
                    placeholder={t("receiptModal.notesPlaceholder")}
                    className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#FFDF00]" />
                </div>

                <div>
                  <label className="text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-1.5 block flex items-center gap-1.5">
                    <Camera className="w-3 h-3" /> {t("receiptModal.attachPhoto")}
                  </label>
                  {!photoPreview ? (
                    <label htmlFor="delivery-note-upload" data-testid="rec-photo-upload-label"
                      className="flex flex-col items-center justify-center gap-2 px-6 py-8 bg-slate-50 border-2 border-dashed border-slate-300 rounded-xl cursor-pointer hover:border-[#FFDF00] hover:bg-[#FFFBE6] transition-colors">
                      <Camera className="w-7 h-7 text-slate-400" />
                      <div className="text-xs text-slate-600 font-semibold">{t("receiptModal.attachPhoto")}</div>
                      <div className="text-[10px] text-slate-400">PNG, JPG · máx 5MB</div>
                      <input id="delivery-note-upload" data-testid="rec-photo-upload-input" type="file" accept="image/*"
                        onChange={handlePhoto} className="hidden" />
                    </label>
                  ) : (
                    <div className="relative bg-white border border-slate-200 rounded-xl overflow-hidden">
                      <img src={photoPreview} alt={t("receiptModal.attachPhoto")} className="w-full max-h-56 object-contain bg-slate-50" />
                      <button data-testid="rec-photo-remove-btn"
                        onClick={() => { setPhotoFile(null); setPhotoPreview(null); setDeliveryNoteUrl(null); }}
                        className="absolute top-2 right-2 w-8 h-8 bg-white/95 hover:bg-white border border-slate-200 rounded-lg flex items-center justify-center shadow-sm">
                        <X className="w-3.5 h-3.5" />
                      </button>
                      <div className="p-2.5 bg-emerald-50 border-t border-emerald-200 flex items-center gap-2">
                        <FileImage className="w-3.5 h-3.5 text-emerald-700 flex-shrink-0" />
                        <span className="text-[11px] text-emerald-800 font-semibold truncate">
                          {uploadingPhoto ? t("receiptModal.uploadingPhoto") : (photoFile?.name || t("receiptModal.photoAttached"))}
                        </span>
                      </div>
                    </div>
                  )}
                </div>

                {hasDiffs && (
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 flex items-start gap-2.5">
                    <AlertTriangle className="w-4 h-4 text-amber-700 flex-shrink-0 mt-0.5" />
                    <div className="text-xs text-amber-900">
                      <span className="font-bold">{t("receiptModal.hasDiffsTitle")}</span>
                      {t("receiptModal.hasDiffsBody")}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          <div className="px-6 py-4 border-t border-slate-200 bg-slate-50 flex items-center justify-end gap-2">
            <button onClick={onClose} className="px-4 py-2 bg-white border border-slate-200 rounded-xl text-sm font-semibold hover:bg-slate-100">
              {t("common.cancel")}
            </button>
            <button data-testid="rec-submit-btn" onClick={submit} disabled={submitting || loading}
              className="btn-primary-chizy px-5 py-2 rounded-xl text-sm flex items-center gap-1.5 disabled:opacity-50">
              <CheckCircle2 className="w-4 h-4" /> {submitting ? t("receiptModal.saving") : t("receiptModal.submit")}
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
